import { FC, useMemo } from 'react';

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';

const DEFAULT_WAITING_TIME_HOURLY_RATE = 85;

interface BillingPreviewProps {
  waitingTime?: Date | null;
  additionalFees?: {
    feeType: string;
    feeAmount: number;
  }[];
  waitingTimeHourlyRate?: number;
}

export const DeliveryUpdateBillingPreview: FC<BillingPreviewProps> = ({
  waitingTime,
  additionalFees = [],
  waitingTimeHourlyRate = DEFAULT_WAITING_TIME_HOURLY_RATE,
}) => {
  const billingItems = useMemo(() => {
    const items: { description: string; amount: number | null }[] = [];

    if (waitingTime instanceof Date) {
      const hours = waitingTime.getHours();
      const minutes = waitingTime.getMinutes();
      const durationHours = hours + minutes / 60;

      if (durationHours > 0) {
        const parts: string[] = [];
        if (hours > 0) parts.push(`${hours} ${hours === 1 ? 'hour' : 'hours'}`);
        if (minutes > 0) parts.push(`${minutes} ${minutes === 1 ? 'minute' : 'minutes'}`);
        const amount = Math.round(durationHours * waitingTimeHourlyRate * 100) / 100;
        items.push({
          description: `Waiting Time ${parts.join(' ')}`,
          amount,
        });
      }
    }

    additionalFees.forEach((fee) => {
      if (fee.feeAmount > 0) {
        items.push({
          description: fee.feeType,
          amount: fee.feeAmount,
        });
      }
    });

    return items;
  }, [waitingTime, additionalFees, waitingTimeHourlyRate]);

  const totalAmount = useMemo(() => {
    return billingItems.reduce((sum, item) => sum + (item.amount ?? 0), 0);
  }, [billingItems]);

  const hasBillableItems = billingItems.some((item) => item.amount !== null);

  if (billingItems.length === 0) {
    return null;
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Billing Preview</CardTitle>
        <p className="text-sm text-muted-foreground">
          Preview only - billing items will be added to invoice after save
        </p>
      </CardHeader>
      <CardContent>
        <div className="space-y-2">
          {billingItems.map((item, index) => (
            <div key={index} className="flex justify-between items-center">
              <span className="text-sm">{item.description}</span>
              {item.amount !== null && (
                <span className="text-sm font-medium">${item.amount.toFixed(2)}</span>
              )}
            </div>
          ))}

          {hasBillableItems && (
            <>
              <hr className="my-2 border-gray-200" />
              <div className="flex justify-between items-center">
                <span className="text-sm font-semibold">Total Preview</span>
                <span className="text-sm font-semibold">${totalAmount.toFixed(2)}</span>
              </div>
            </>
          )}
        </div>
      </CardContent>
    </Card>
  );
};
